/**
 * Configurações globais da aplicação
 * Centralizadas para facilitar manutenção e deployment
 */

// Informações da aplicação
export const APP_CONFIG = {
  name: import.meta.env.VITE_APP_NAME || 'LavadorPro',
  version: import.meta.env.VITE_APP_VERSION || '1.0.0',
  domain: import.meta.env.VITE_APP_DOMAIN || 'localhost:3000',
} as const;

// Configurações da API
export const API_CONFIG = {
  baseURL: import.meta.env.VITE_API_BASE_URL || 'http://192.168.0.236:8000/v1',
  domain: import.meta.env.VITE_API_DOMAIN || '192.168.0.236:8000',
  timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 30000,
  retryAttempts: Number(import.meta.env.VITE_NETWORK_RETRY_ATTEMPTS) || 3,
  retryDelay: Number(import.meta.env.VITE_NETWORK_RETRY_DELAY) || 1000,
} as const;

// Endpoints da API
export const API_ENDPOINTS = {
  // Autenticação
  AUTH: {
    MASTER_LOGIN: '/auth/master/login',
    ENTERPRISE_LOGIN: '/auth/enterprise/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    ME: '/auth/me',
  },
  
  // KyoZap
  KYOZAP: {
    BASE: '/kyozap',
    CONNECT: '/kyozap/connect',
    STATUS: '/kyozap/status',
    SEND_MESSAGE: '/kyozap/send',
    WEBHOOK: '/kyozap/webhook',
  },
  
  // Dashboard
  DASHBOARD: {
    STATS: '/dashboard/stats',
    OVERVIEW: '/dashboard/overview',
  },
  
  // Clientes
  CUSTOMERS: {
    LIST: '/customers',
    CREATE: '/customers',
    UPDATE: '/customers/:id',
    DELETE: '/customers/:id',
    DETAILS: '/customers/:id',
  },
  
  // Financeiro
  FINANCIAL: {
    OVERVIEW: '/financial/overview',
    TRANSACTIONS: '/financial/transactions',
    PAYMENTS: '/financial/payments',
  },
} as const;

// Configurações de autenticação
export const AUTH_CONFIG = {
  tokenKey: import.meta.env.VITE_JWT_STORAGE_KEY || 'token',
  refreshTokenKey: import.meta.env.VITE_REFRESH_TOKEN_STORAGE_KEY || 'refresh_token',
  autoRefreshThreshold: Number(import.meta.env.VITE_AUTO_REFRESH_THRESHOLD) || 5,
  sessionTimeout: Number(import.meta.env.VITE_SESSION_TIMEOUT) || 30,
  inactivityWarning: Number(import.meta.env.VITE_INACTIVITY_WARNING) || 25,
} as const;

// Configurações do KyoZap
export const KYOZAP_CONFIG = {
  apiKey: import.meta.env.VITE_KYOZAP_API_KEY || '',
  apiUrl: import.meta.env.VITE_KYOZAP_API_URL || 'http://192.168.0.236:8000/v1/kyozap',
  webhookUrl: import.meta.env.VITE_KYOZAP_WEBHOOK_URL || 'http://localhost:3000/webhook/kyozap',
  wsUrl: import.meta.env.VITE_KYOZAP_WS_URL || 'ws://localhost:8001/ws',
  maxRetries: Number(import.meta.env.VITE_KYOZAP_MAX_RETRIES) || 3,
  timeout: Number(import.meta.env.VITE_KYOZAP_TIMEOUT) || 30,
} as const;

// Configurações OAuth
export const OAUTH_CONFIG = {
  google: {
    clientId: import.meta.env.VITE_GOOGLE_CLIENT_ID || '',
    redirectUri: import.meta.env.VITE_GOOGLE_REDIRECT_URI || 'http://localhost:3000/auth/google/callback',
    scopes: import.meta.env.VITE_GOOGLE_SCOPES || 'openid email profile',
  },
  facebook: {
    appId: import.meta.env.VITE_FACEBOOK_APP_ID || '',
    redirectUri: import.meta.env.VITE_FACEBOOK_REDIRECT_URI || 'http://localhost:3000/auth/facebook/callback',
    scopes: import.meta.env.VITE_FACEBOOK_SCOPES || 'email,public_profile',
  },
  github: {
    clientId: import.meta.env.VITE_GITHUB_CLIENT_ID || '',
    redirectUri: import.meta.env.VITE_GITHUB_REDIRECT_URI || 'http://localhost:3000/auth/github/callback',
    scopes: import.meta.env.VITE_GITHUB_SCOPES || 'user:email',
  },
} as const;

// Configurações de payment gateways
export const PAYMENT_CONFIG = {
  stripe: {
    publishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '',
    successUrl: import.meta.env.VITE_STRIPE_SUCCESS_URL || 'http://localhost:3000/payment/success',
    cancelUrl: import.meta.env.VITE_STRIPE_CANCEL_URL || 'http://localhost:3000/payment/cancel',
    currency: import.meta.env.VITE_STRIPE_CURRENCY || 'brl',
  },
  mercadoPago: {
    publicKey: import.meta.env.VITE_MERCADOPAGO_PUBLIC_KEY || '',
    successUrl: import.meta.env.VITE_MERCADOPAGO_SUCCESS_URL || 'http://localhost:3000/payment/success',
    cancelUrl: import.meta.env.VITE_MERCADOPAGO_CANCEL_URL || 'http://localhost:3000/payment/cancel',
    currency: import.meta.env.VITE_MERCADOPAGO_CURRENCY || 'BRL',
  },
  paypal: {
    clientId: import.meta.env.VITE_PAYPAL_CLIENT_ID || '',
    currency: import.meta.env.VITE_PAYPAL_CURRENCY || 'BRL',
    intent: import.meta.env.VITE_PAYPAL_INTENT || 'capture',
    environment: import.meta.env.VITE_PAYPAL_ENVIRONMENT || 'sandbox',
  },
} as const;

// Configurações de storage
export const STORAGE_CONFIG = {
  prefix: import.meta.env.VITE_STORAGE_PREFIX || 'lavadorpro_',
  encryption: import.meta.env.VITE_STORAGE_ENCRYPTION === 'true',
  defaultTTL: Number(import.meta.env.VITE_STORAGE_DEFAULT_TTL) || 24,
  maxSize: Number(import.meta.env.VITE_STORAGE_MAX_SIZE) || 50,
} as const;

// Feature flags
export const FEATURE_FLAGS = {
  darkMode: import.meta.env.VITE_FEATURE_DARK_MODE === 'true',
  notifications: import.meta.env.VITE_FEATURE_NOTIFICATIONS === 'true',
  offlineMode: import.meta.env.VITE_FEATURE_OFFLINE_MODE === 'true',
  beta: import.meta.env.VITE_FEATURE_BETA === 'true',
  debugMode: import.meta.env.VITE_DEBUG_MODE === 'true',
} as const;

// Configurações de logging
export const LOG_CONFIG = {
  level: import.meta.env.VITE_LOG_LEVEL || 'info',
  enableConsole: import.meta.env.NODE_ENV === 'development' || FEATURE_FLAGS.debugMode,
  enableRemote: import.meta.env.NODE_ENV === 'production',
  maxRetries: 3,
} as const;

// Configurações de CDN
export const CDN_CONFIG = {
  baseUrl: import.meta.env.VITE_CDN_BASE_URL || '',
  imagesUrl: import.meta.env.VITE_CDN_IMAGES_URL || '',
  staticUrl: import.meta.env.VITE_CDN_STATIC_URL || '',
} as const;

// URLs base para facilitar construção de endpoints
export const buildApiUrl = (endpoint: string): string => {
  const baseURL = API_CONFIG.baseURL.replace(/\/$/, ''); // Remove trailing slash
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseURL}${cleanEndpoint}`;
};

// Helper para URLs do KyoZap
export const buildKyoZapUrl = (endpoint: string): string => {
  const baseURL = KYOZAP_CONFIG.apiUrl.replace(/\/$/, '');
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${baseURL}${cleanEndpoint}`;
};

// Exportação para debug
if (FEATURE_FLAGS.debugMode) {
  console.log('🔧 Configurações carregadas:', {
    APP_CONFIG,
    API_CONFIG,
    AUTH_CONFIG,
    KYOZAP_CONFIG,
    FEATURE_FLAGS,
    LOG_CONFIG,
  });
}
